import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, createVNode as _createVNode, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, Fragment as _Fragment, unref as _unref, normalizeClass as _normalizeClass, withModifiers as _withModifiers, resolveComponent as _resolveComponent, withCtx as _withCtx } from "vue"

const _hoisted_1 = ["aria-busy"]
const _hoisted_2 = { class: "manager-reports-ant__action-bar" }
const _hoisted_3 = ["href"]
const _hoisted_4 = ["innerHTML"]
const _hoisted_5 = { class: "manager-reports-ant__table-wrapper personal-area-table-wrapper" }
const _hoisted_6 = {
  key: 0,
  class: "manager-reports-ant__list--empty"
}
const _hoisted_7 = { class: "manager-reports-ant__list--empty-title" }
const _hoisted_8 = {
  key: 0,
  class: "manager-reports-ant__list--empty-description"
}
const _hoisted_9 = { class: "manager-reports-ant__status-text" }
const _hoisted_10 = ["src"]

import { computed, onMounted, ref } from 'vue';
import type { TableColumnsType } from 'ant-design-vue';
import FilterIcon from '@/js/components/icons/FilterIcon.vue';
import {
  showStcErrorNotification,
} from '@/js/components/stc-notification';
import { getAssetsPath } from '@/js/utils/getAssetsDir';
import { formatMatchCountSummary } from '@/js/utils/search-highlight';
import SearchInput from '../SearchInput.vue';
import Pagination from '../Pagination.vue';
import ReportDetailView from './ReportDetailView.vue';
import ReportsFilterDrawer from './ReportsFilterDrawer.vue';
import {
  fetchManagerReportsList,
  getManagerReportsExportUrl,
} from './manager-reports-api';
import {
  formatManagerReportDate,
  filterManagerReports,
  getStatusModifier,
} from './map-manager-report';
import {
  MANAGER_REPORTS_PAGE_SIZE,
  MANAGER_REPORT_STATUS_OPTIONS,
  getManagerReportYearOptions,
  type ManagerReportRow,
  type ManagerReportsClusterOption,
  type ManagerReportsFilter,
} from './types';

const SEARCH_MIN_LENGTH = 3;

export default /*@__PURE__*/_defineComponent({
  __name: 'index',
  setup(__props) {

const TABLE_SCROLL = { x: 1050, y: 500 };
const pageSize = MANAGER_REPORTS_PAGE_SIZE;
const SIGNED_ICON = getAssetsPath('/assets/images/icons/resident-lk/signed.svg');
const NOT_SIGNED_ICON = getAssetsPath('/assets/images/icons/resident-lk/not-signed.svg');

const columns: TableColumnsType<ManagerReportRow> = [
  {
    title: 'Резидент',
    key: 'residentName',
    dataIndex: 'residentName',
    width: 240,
  },
  {
    title: 'Дата подачи',
    key: 'createdAt',
    dataIndex: 'createdAt',
    width: 160,
  },
  {
    title: 'Отчетный период',
    key: 'reportYear',
    dataIndex: 'reportYear',
    width: 160,
  },
  {
    title: 'Статус',
    key: 'status',
    dataIndex: 'status',
    width: 200,
    customCell: () => ({
      class: 'manager-reports-ant__status-cell',
    }),
  },
  {
    title: 'Эл. подпись',
    key: 'signature',
    dataIndex: 'isSign',
    width: 150,
  },
];

const loading = ref(false);
const errorMessage = ref('');
const allItems = ref<ManagerReportRow[]>([]);
const clusters = ref<ManagerReportsClusterOption[]>([]);
const searchQuery = ref('');
const appliedSearchQuery = ref('');
const currentPage = ref(1);
const filterOpen = ref(false);
const appliedFilter = ref<ManagerReportsFilter>({});
const selectedReport = ref<ManagerReportRow | null>(null);

const statusOptions = MANAGER_REPORT_STATUS_OPTIONS.map((option) => ({
  value: option.id,
  label: option.name,
}));

const yearOptions = computed(() => getManagerReportYearOptions());

const isFilterApplied = computed(() =>
  Boolean(appliedFilter.value.clusterId || appliedFilter.value.statusId || appliedFilter.value.year),
);

const hasActiveSearch = computed(() => appliedSearchQuery.value.trim().length >= SEARCH_MIN_LENGTH);

const searchSummaryMarkup = computed(() =>
  formatMatchCountSummary(filteredItems.value.length, appliedSearchQuery.value.trim()),
);

const shouldShowTable = computed(() => loading.value || filteredItems.value.length > 0);

const shouldShowEmpty = computed(
  () => !loading.value && filteredItems.value.length === 0 && !hasActiveSearch.value,
);

const emptyState = computed(() => {
  if (errorMessage.value) {
    return {
      title: errorMessage.value,
      description: '',
    };
  }

  if (isFilterApplied.value) {
    return {
      title: '0 результатов',
      description: 'Измените значения в фильтре.',
    };
  }

  return {
    title: 'Отчётов нет',
    description: '',
  };
});

const exportUrl = computed(() => getManagerReportsExportUrl());

const filteredItems = computed(() =>
  filterManagerReports(allItems.value, {
    q: appliedSearchQuery.value,
    filter: appliedFilter.value,
  }),
);

const paginatedItems = computed(() => {
  const start = (currentPage.value - 1) * pageSize;
  return filteredItems.value.slice(start, start + pageSize);
});

function formatReportYear(year?: string | null): string {
  return year ? `${year} г.` : '—';
}

function isReportSigned(record: ManagerReportRow): boolean {
  return Boolean(record.isSign ?? record.isSigned);
}

async function loadReports() {
  loading.value = true;
  errorMessage.value = '';

  try {
    const data = await fetchManagerReportsList({
      q: appliedSearchQuery.value,
      filter: appliedFilter.value,
    });

    allItems.value = data.items;
    clusters.value = data.clusters;
    currentPage.value = 1;
  } catch (error) {
    allItems.value = [];
    const message =
      error instanceof Error ? error.message : 'Не удалось загрузить список отчётов';
    errorMessage.value = message;
    showStcErrorNotification(message);
  } finally {
    loading.value = false;
  }
}

function handleSearch(query: string) {
  appliedSearchQuery.value = query;
  currentPage.value = 1;
}

function handlePageChange(page: number) {
  currentPage.value = page;
}

function handleFilterApply(filter: ManagerReportsFilter) {
  appliedFilter.value = filter;
  currentPage.value = 1;
  void loadReports();
}

function getRowProps(record: ManagerReportRow) {
  return {
    onClick: () => {
      selectedReport.value = record;
    },
  };
}

function handleDetailBack() {
  selectedReport.value = null;
}

function handleDetailUpdated() {
  void loadReports();
}

onMounted(() => {
  void loadReports();
});

return (_ctx: any,_cache: any) => {
  const _component_a_table = _resolveComponent("a-table")!

  return (selectedReport.value)
    ? (_openBlock(), _createBlock(ReportDetailView, {
        key: 0,
        report: selectedReport.value,
        onBack: handleDetailBack,
        onUpdated: handleDetailUpdated
      }, null, 8, ["report"]))
    : (_openBlock(), _createElementBlock("div", {
        key: 1,
        class: _normalizeClass(["manager-reports-ant", { 'is-loading': loading.value }]),
        "aria-busy": loading.value ? 'true' : undefined
      }, [
        _cache[7] || (_cache[7] = _createElementVNode("h2", { class: "manager-reports-ant__title" }, "Отчеты", -1)),
        _createElementVNode("div", _hoisted_2, [
          _createVNode(SearchInput, {
            modelValue: searchQuery.value,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((searchQuery).value = $event)),
            placeholder: "Найти...",
            onSearch: handleSearch
          }, null, 8, ["modelValue"]),
          _createElementVNode("button", {
            type: "button",
            class: "btn btn-secondary btn-medium",
            onClick: _cache[1] || (_cache[1] = ($event: any) => (filterOpen.value = true))
          }, [
            _createVNode(FilterIcon, { filled: isFilterApplied.value }, null, 8, ["filled"]),
            _cache[5] || (_cache[5] = _createElementVNode("span", null, "Фильтр", -1))
          ]),
          _createElementVNode("a", {
            href: exportUrl.value,
            class: "btn btn-secondary btn-medium manager-reports-ant__download-btn",
            target: "_blank",
            rel: "noopener",
            title: "Скачать все отчёты в Excel"
          }, " Скачать отчеты (.xlsx) ", 8, _hoisted_3)
        ]),
        (hasActiveSearch.value)
          ? (_openBlock(), _createElementBlock("p", {
              key: 0,
              class: "search-summary manager-reports-ant__search-summary",
              innerHTML: searchSummaryMarkup.value
            }, null, 8, _hoisted_4))
          : _createCommentVNode("", true),
        _createElementVNode("div", _hoisted_5, [
          (shouldShowEmpty.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                _createElementVNode("div", _hoisted_7, _toDisplayString(emptyState.value.title), 1),
                (emptyState.value.description)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_8, _toDisplayString(emptyState.value.description), 1))
                  : _createCommentVNode("", true)
              ]))
            : (shouldShowTable.value)
              ? (_openBlock(), _createBlock(_component_a_table, {
                  key: 1,
                  class: "manager-reports-ant__table",
                  columns: columns,
                  bordered: true,
                  "data-source": paginatedItems.value,
                  loading: loading.value,
                  pagination: false,
                  scroll: TABLE_SCROLL,
                  "custom-row": getRowProps,
                  "row-key": "id"
                }, {
                  bodyCell: _withCtx(({ column, record }) => [
                    (column.key === 'residentName')
                      ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                          _createTextVNode(_toDisplayString(record.residentName), 1)
                        ], 64))
                      : (column.key === 'createdAt')
                        ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                            _createTextVNode(_toDisplayString(_unref(formatManagerReportDate)(record.createdAt)), 1)
                          ], 64))
                        : (column.key === 'reportYear')
                          ? (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                              _createTextVNode(_toDisplayString(formatReportYear(record.reportYear)), 1)
                            ], 64))
                          : (column.key === 'status')
                            ? (_openBlock(), _createElementBlock("span", {
                                key: 3,
                                class: _normalizeClass(["manager-reports-ant__status", `manager-reports-ant__status--${_unref(getStatusModifier)(record.status)}`])
                              }, [
                                _cache[6] || (_cache[6] = _createElementVNode("span", {
                                  class: "manager-reports-ant__status-dot",
                                  "aria-hidden": "true"
                                }, null, -1)),
                                _createElementVNode("span", _hoisted_9, _toDisplayString(record.status || '—'), 1)
                              ], 2))
                            : (column.key === 'signature')
                              ? (_openBlock(), _createElementBlock("span", {
                                  key: 4,
                                  class: _normalizeClass(["manager-reports-ant__signature", 
                isReportSigned(record)
                  ? 'manager-reports-ant__signature--signed'
                  : 'manager-reports-ant__signature--unsigned'
              ]),
                                  onClick: _cache[2] || (_cache[2] = _withModifiers(() => {}, ["stop"]))
                                }, [
                                  _createElementVNode("img", {
                                    src: isReportSigned(record) ? _unref(SIGNED_ICON) : _unref(NOT_SIGNED_ICON),
                                    alt: "",
                                    "aria-hidden": "true"
                                  }, null, 8, _hoisted_10),
                                  _createTextVNode(" " + _toDisplayString(isReportSigned(record) ? 'Подписан' : 'Не подписан'), 1)
                                ], 2))
                              : _createCommentVNode("", true)
                  ]),
                  _: 1
                }, 8, ["data-source", "loading"]))
              : _createCommentVNode("", true)
        ]),
        (filteredItems.value.length > _unref(pageSize))
          ? (_openBlock(), _createBlock(Pagination, {
              key: 1,
              modelValue: currentPage.value,
              "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((currentPage).value = $event)),
              class: "manager-reports-ant__pagination personal-area-table-pagination",
              "total-count": filteredItems.value.length,
              "page-size": _unref(pageSize),
              onChange: handlePageChange
            }, null, 8, ["modelValue", "total-count", "page-size"]))
          : _createCommentVNode("", true),
        _createVNode(ReportsFilterDrawer, {
          open: filterOpen.value,
          "onUpdate:open": _cache[4] || (_cache[4] = ($event: any) => ((filterOpen).value = $event)),
          clusters: clusters.value,
          "status-options": _unref(statusOptions),
          "year-options": yearOptions.value,
          filter: appliedFilter.value,
          onApply: handleFilterApply
        }, null, 8, ["open", "clusters", "status-options", "year-options", "filter"])
      ], 10, _hoisted_1))
}
}

})